import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';
import {
  CivilStateType,
  Country,
  EducationLevel,
  Gender,
  Language,
  LocationItem,
  LocationSearch,
  Nationality,
  OptionList,
  OptionListValue,
} from '@app/core/types/common/common.types';
import { DriverLicenseType } from '@app/documents/models/documents.type';

@Injectable({ providedIn: 'root' })
export class CommonService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.url.api}/common`;

  getCountries(): Observable<Country[]> {
    return this.http.get(`${this.url}/country`).pipe(map((response: Country[]) => response));
  }

  getGenders(): Observable<Gender[]> {
    return this.http.get(`${this.url}/gender`).pipe(map((response: Gender[]) => response));
  }

  getApplicationLanguage(): Observable<Language[]> {
    return this.http.get(`${this.url}/applicationlanguage`).pipe(map((response: Language[]) => response));
  }

  getSpokenLanguage(): Observable<Language[]> {
    return this.http.get(`${this.url}/spokenlanguage`).pipe(map((response: Language[]) => response));
  }

  getNationalities(): Observable<Nationality[]> {
    return this.http.get(`${this.url}/nationality`).pipe(map((response: Nationality[]) => response));
  }

  getDriversLicenses(): Observable<DriverLicenseType[]> {
    return this.http
      .get(`${this.url}/driverslicense`)
      .pipe(map((response: { driversLicenses: DriverLicenseType[] }) => response.driversLicenses));
  }

  getCivilStates(): Observable<CivilStateType[]> {
    return this.http.get<CivilStateType[]>(`${this.url}/civilstate`);
  }

  searchFunctions(query: string, includeOriginalQuery = false): Observable<string[]> {
    return this.http
      .get(`${this.url}/function/search`, {
        params: new HttpParams().append('query', query).append('includeOriginalQuery', includeOriginalQuery.toString()),
      })
      .pipe(map((response: { functions: string[] }) => response.functions));
  }

  searchLocations(query: LocationSearch): Observable<LocationItem[]> {
    let params = new HttpParams().set('query', query.query);
    if (query.countryId) {
      params = params.append('countryId', query.countryId);
    }
    return this.http
      .get<{ locations: LocationItem[] }>(`${this.url}/location/search`, {
        params: params,
      })
      .pipe(map((response) => response.locations));
  }

  getEducationLevels(): Observable<EducationLevel[]> {
    return this.http.get<EducationLevel[]>(`${this.url}/educationlevel/eu`);
  }

  getOptionListValues(type: string, context?: string): Observable<OptionListValue[]> {
    const options = context ? { params: new HttpParams().set('context', context) } : {};
    return this.http
      .get<OptionList>(`${this.url}/option-list/${type}`, options)
      .pipe(map((response) => response.options));
  }
}
